/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * MIGRATION DOCUMENT PACK (Edgewater phase 1). The folders a firm's zip pack is
 * read against, how a folder name is matched, the person a subfolder names, and
 * whether a document's reading fits the folder it was filed in. Shared by the
 * server (api/_migration-pack.js, api/migrate-client.js, api/df-process.js) and
 * the dashboard (the preview and the document list). Pure: no I/O.
 *
 * Every string an officer reads is written without dashes.
 */

// The limits the user approved (09/10/2026). Shown to the officer as they are.
// The zip came down from 100 MB to 50 MB (10/10/2026): the zip is stored whole
// in the onboarding-documents bucket, whose file size limit is 50 MB.
export const PACK_LIMITS = Object.freeze({
  zipBytes: 50 * 1024 * 1024,
  files: 200,
  fileBytes: 25 * 1024 * 1024,
  totalBytes: 300 * 1024 * 1024,
  ratio: 100,
});

export const PACK_LIMITS_TEXT = 'A zip of up to 50 MB, with at most 200 files, each file up to 25 MB and 300 MB unpacked in total. A file that expands more than 100 times its packed size is refused.';

const packMb = (n) => (n / (1024 * 1024)).toFixed(1) + ' MB';

/**
 * The browser's check before a zip is uploaded: null when the zip is within the
 * limit, else the sentence the officer is shown. Nothing has been uploaded and
 * no case created when it refuses.
 */
export function zipTooLargeReason(bytes) {
  if (!(bytes > PACK_LIMITS.zipBytes)) return null;
  return `This zip is ${packMb(bytes)}, over the ${PACK_LIMITS.zipBytes / (1024 * 1024)} MB limit for a zip. Nothing has been uploaded and no case has been created. Make the scans smaller, or leave out files the analysis does not need, then choose the zip again.`;
}

// The allowed file types, by extension, with the type each is stored as.
export const PACK_TYPES = Object.freeze({
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  heic: 'image/heic',
  heif: 'image/heif',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  doc: 'application/msword',
  xls: 'application/vnd.ms-excel',
});
export const PACK_TYPES_TEXT = 'PDF, images (PNG, JPEG, WEBP, TIFF, HEIC) and Office files (docx, xlsx, pptx, doc, xls)';

// internal: the kind of internal record a folder's files become. Internal files
// are never shown to the client and never given to the document analysis.
export const PACK_FOLDERS = Object.freeze([
  { n: 1, key: 'company', label: 'Company Information', type: 'Company document', names: ['company information', 'company info', 'company', 'corporate documents', 'corporate information', 'corporate'] },
  { n: 2, key: 'poa', label: 'POA (proof of address)', type: 'Proof of address', names: ['poa proof of address', 'proof of address poa', 'poa', 'proof of address', 'proofs of address', 'address'] },
  { n: 3, key: 'ubo', label: 'UBO', type: 'Beneficial owner document', person: true, role: 'beneficial owner', names: ['ubo', 'ubos', 'beneficial owner', 'beneficial owners', 'ultimate beneficial owner', 'ultimate beneficial owners'] },
  { n: 4, key: 'directors', label: 'Directors', type: 'Director document', person: true, role: 'director', names: ['directors', 'director'] },
  { n: 5, key: 'authorised', label: 'Authorised Persons', type: 'Authorised person document', person: true, role: 'authorised signatory', names: ['authorised persons', 'authorized persons', 'authorised person', 'authorized person', 'authorised signatories', 'authorized signatories', 'signatories'] },
  { n: 6, key: 'aof', label: 'Account Opening Form', type: 'Account opening form', names: ['account opening form', 'account opening forms', 'account opening', 'aof'] },
  { n: 7, key: 'legal', label: 'Legal Paperwork', type: 'Legal document', names: ['legal paperwork', 'legal documents', 'legal'] },
  { n: 8, key: 'signoff', label: 'Compliance Signoff', type: 'Prior compliance sign off (internal record)', internal: 'prior_signoff', names: ['compliance signoff', 'compliance sign off', 'compliance signoffs', 'compliance sign offs', 'signoff', 'sign off', 'compliance approval'] },
  { n: 9, key: 'screening', label: 'Screening', type: 'Prior screening dossier (internal record)', internal: 'prior_screening', optional: true, names: ['screening', 'screenings', 'screening reports', 'world check', 'worldcheck', 'world check one', 'lseg'] },
]);
export const UNFILED = Object.freeze({ n: null, key: 'unfiled', label: 'Unfiled', type: 'To be classified by the analysis' });

export const INTERNAL_KIND_LABEL = Object.freeze({
  prior_signoff: 'Internal record: the firm’s prior compliance sign off. Officers only, never shown to the client, not given to the analysis.',
  prior_screening: 'Internal record: the firm’s prior screening dossier. Officers only, never shown to the client, not given to the analysis.',
});

export const folderByKey = (key) => PACK_FOLDERS.find(f => f.key === key) || (key === 'unfiled' ? UNFILED : null);
export const folderByNumber = (n) => PACK_FOLDERS.find(f => f.n === Number(n)) || null;

// "2 POA (proof of address)" -> "2 poa proof of address"; "01_Company-Info" -> "01 company info".
export function normaliseFolderName(name) {
  return String(name || '')
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const byName = (rest) => {
  if (!rest) return null;
  return PACK_FOLDERS.find(f => f.names.includes(rest)) || null;
};

// A top folder of the pack, matched by its number or its name.
// Returns { folder, note } or null. note says why when number and name disagree.
export function matchFolder(name) {
  const norm = normaliseFolderName(name);
  if (!norm) return null;
  const m = /^0*(\d{1,2})(?:\s+(.*))?$/.exec(norm);
  if (m) {
    const num = folderByNumber(m[1]);
    const rest = (m[2] || '').trim();
    const named = byName(rest);
    if (num && (!rest || !named || named === num)) return { folder: num, note: null };
    if (named && named !== num) return { folder: named, note: `numbered ${m[1]} but named ${named.label}, so filed by its name` };
    return null;
  }
  const named = byName(norm);
  return named ? { folder: named, note: null } : null;
}

// Mac and Windows system files: skipped and listed, never filed.
export function systemFileReason(path) {
  const segs = String(path || '').split('/').filter(Boolean);
  const base = segs[segs.length - 1] || '';
  if (segs.some(s => s === '__MACOSX')) return 'a Mac system folder (__MACOSX)';
  if (segs.some(s => /^(\.Spotlight-V100|\.Trashes|\.fseventsd|\$RECYCLE\.BIN|System Volume Information)$/i.test(s))) return 'a system folder';
  if (base === '.DS_Store') return 'a Mac system file (.DS_Store)';
  if (base.startsWith('._')) return 'a Mac system file (resource fork)';
  if (/^(thumbs\.db|ehthumbs\.db|desktop\.ini)$/i.test(base)) return 'a Windows system file (' + base + ')';
  if (base.startsWith('~$')) return 'an Office lock file (' + base + ')';
  if (/^Icon\r?$/.test(base)) return 'a Mac system file (folder icon)';
  if (base.startsWith('.')) return 'a hidden system file (' + base + ')';
  return null;
}

/**
 * Place each file of the pack. entries: [{ path }] for files only (directories,
 * system files and refused paths already removed). Returns a plan entry per
 * input: { path, folder (a PACK_FOLDERS entry or UNFILED), person, name, note }
 * plus the wrapping folder that was set aside, if any.
 */
export function placeFiles(entries) {
  const list = (Array.isArray(entries) ? entries : []).map(e => ({ ...e, segs: String(e.path).split('/').filter(Boolean) }));
  let wrapper = null;
  // One wrapping folder is allowed: every file sits under the same first folder,
  // and that folder is not itself one of the pack's folders.
  if (list.length && list.every(e => e.segs.length >= 2)) {
    const first = list[0].segs[0];
    if (list.every(e => e.segs[0] === first) && !matchFolder(first)) wrapper = first;
  }
  const placed = list.map(e => {
    const segs = wrapper ? e.segs.slice(1) : e.segs;
    const name = segs[segs.length - 1];
    if (segs.length < 2) return { path: e.path, folder: UNFILED, person: null, name, note: 'outside the pack folders' };
    const hit = matchFolder(segs[0]);
    if (!hit) return { path: e.path, folder: UNFILED, person: null, name, note: `in a folder the pack does not use ("${segs[0]}")` };
    const f = hit.folder;
    let person = null;
    let note = hit.note;
    if (f.person) {
      if (segs.length >= 3) person = segs[1].trim() || null;
      else note = (note ? note + '; ' : '') + 'no person subfolder, so no person is suggested';
    }
    return { path: e.path, folder: f, person, name, note };
  });
  return { wrapper, placed };
}

// The hint the document analysis is given for a filed document (never for an
// internal record, which the analysis never sees).
export function packHintText(hint) {
  if (!hint || !hint.folderLabel) return '';
  const where = hint.folder ? `folder ${hint.folder} (${hint.folderLabel})` : `the ${hint.folderLabel} group`;
  const who = hint.person ? `, in the subfolder for ${hint.person} (${hint.role || 'a person on the file'})` : '';
  return `FILING HINT: the firm filed this document in its migration pack under ${where}${who}. Treat this as a hint only. Classify the document from its own content; if the content does not match where it was filed, say what the document actually is.`;
}

// Does a document's reading fit the folder it was filed in? Deterministic.
// Returns { fits: true | false | null, reason }. null: not read yet, or no
// folder to compare (Unfiled, internal records).
const FIT = {
  company: /incorporat|registr|register|memorandum|articles|constitution|good standing|incumbency|shareholder|certificate|extract|statute|bylaw|by law|licen[cs]e|annual return|financial statement|accounts|organi[sz]ation(al)? chart|structure chart|ownership chart|trust deed|partnership|company|corporate|business|tax|vat|lei/i,
  poa: /address|utility|bank statement|council tax|lease|tenancy|rental|bill|statement|residence/i,
  person: /passport|identity|identification|id card|national id|driv|licen[cs]e|proof of address|address|utility|bank statement|cv|curriculum|resume|declaration|source of (wealth|funds)|register|power of attorney|signator|resolution|specimen|signature|selfie|residence permit|visa|birth|marriage|kyc|personal/i,
  aof: /application|account opening|opening form|form|questionnaire|onboarding|fccq|wolfsberg/i,
  legal: /agreement|contract|legal|opinion|resolution|power of attorney|deed|terms|letter|court|judg|minutes|memorandum|articles|constitution|declaration|certificate/i,
};
export function packFolderFit(hint, analysis) {
  if (!hint || !hint.folderKey || hint.folderKey === 'unfiled' || hint.internal) return { fits: null, reason: null };
  const a = analysis && typeof analysis === 'object' ? analysis : null;
  if (!a || a._analysisError || /analysis failed/i.test(JSON.stringify(a.keyFindings || []))) return { fits: null, reason: null };
  const docType = String(a.docType || '').trim();
  if (!docType) return { fits: null, reason: null };
  const re = FIT[['ubo', 'directors', 'authorised'].includes(hint.folderKey) ? 'person' : hint.folderKey];
  const label = hint.folder ? `${hint.folder} ${hint.folderLabel}` : hint.folderLabel;
  if (re && !re.test(docType)) return { fits: false, reason: `Filed in ${label}, but the analysis read it as ${docType}. Check where it belongs; nothing was moved.` };
  if (hint.person) {
    const tokens = String(hint.person).toLowerCase().split(/[^a-zÀ-ɏ]+/).filter(t => t.length >= 3);
    const text = JSON.stringify(a).toLowerCase();
    if (tokens.length && !tokens.some(t => text.includes(t))) return { fits: false, reason: `Filed under ${hint.person} in ${label}, but the reading does not name this person. Check whose document it is; nothing was moved.` };
  }
  return { fits: true, reason: null };
}
